import type { ViewerBootstrap, ViewerContent } from './types';

// Public viewer API client. The viewer token (gate progress) is persisted
// per-slug in localStorage; the visit id per-tab in sessionStorage.

const tokenKey = (slug: string) => `ds_vt_${slug}`;

export function getViewerToken(slug: string): string | null {
  return localStorage.getItem(tokenKey(slug));
}

export function setViewerToken(slug: string, token: string): void {
  localStorage.setItem(tokenKey(slug), token);
}

export class UnavailableError extends Error {
  constructor() {
    super('unavailable');
  }
}

async function vrequest<T>(slug: string, path: string, init: RequestInit = {}): Promise<T> {
  const token = getViewerToken(slug);
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'X-Viewer-Token': token } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 404) throw new UnavailableError();
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  const data = (await res.json()) as T & { token?: string };
  if (data.token) setViewerToken(slug, data.token);
  return data;
}

export const viewerApi = {
  bootstrap: (slug: string) => vrequest<ViewerBootstrap>(slug, `/api/view/${slug}`),
  identify: (slug: string, name: string, email: string) =>
    vrequest<ViewerBootstrap>(slug, `/api/view/${slug}/identify`, {
      method: 'POST',
      body: JSON.stringify({ name, email }),
    }),
  signNda: (slug: string, typedName: string) =>
    vrequest<ViewerBootstrap & { signedCopyUrl?: string }>(slug, `/api/view/${slug}/nda/sign`, {
      method: 'POST',
      body: JSON.stringify({ agree: true, typedName }),
    }),
  content: (slug: string) =>
    vrequest<{ token: string; content: ViewerContent }>(slug, `/api/view/${slug}/content`),
  startVisit: (slug: string) =>
    vrequest<{ visitId: string; startedAt: string }>(slug, `/api/view/${slug}/visits`, {
      method: 'POST',
    }),
  download: (slug: string, docId: string, visitId?: string, startedAt?: string) => {
    const qs =
      visitId && startedAt
        ? `?visitId=${encodeURIComponent(visitId)}&startedAt=${encodeURIComponent(startedAt)}`
        : '';
    return vrequest<{ url: string }>(slug, `/api/view/${slug}/download${qs}`, {
      method: 'POST',
      body: JSON.stringify({ docId }),
    });
  },
  signedCopy: (slug: string) =>
    vrequest<{ url: string }>(slug, `/api/view/${slug}/nda/signed-copy`),
};
